import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { Mail, AlertCircle, Loader2 } from "lucide-react";
import "../../LoginScreen.css";
import { emailStepSchema, type EmailStepInput } from "../../../../lib/validation/schemas/auth.schema";

interface EmailStepProps {
  loading: boolean;
  serverError: string;
  onSubmit: (email: string) => void;
  onGoogleSignIn: () => void;
}

export default function EmailStep({ loading, serverError, onSubmit, onGoogleSignIn }: EmailStepProps) {
  const { t } = useTranslation(["auth", "common"]);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EmailStepInput>({
    resolver: zodResolver(emailStepSchema),
    defaultValues: { email: "" },
    mode: "onSubmit",
  });

  const errorMessage = errors.email?.message ?? serverError;

  return (
    <>
      <div className="ls-form-header">
        <h2 className="ls-welcome-title">{t("email.welcomeTitle")}</h2>
        <p className="ls-welcome-subtitle">{t("email.welcomeSubtitle")}</p>
      </div>

      {errorMessage && (
        <div className="ls-error-banner">
          <AlertCircle size={16} className="ls-error-icon" />
          <span>{errorMessage}</span>
        </div>
      )}

      <button
        type="button"
        onClick={onGoogleSignIn}
        disabled={loading}
        className="ls-google-btn"
      >
        <svg width="18" height="18" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
        <span>{t("email.continueWithGoogle")}</span>
      </button>

      <div className="ls-divider">
        <span className="ls-divider__line" />
        <span className="ls-divider__text">{t("email.or")}</span>
        <span className="ls-divider__line" />
      </div>

      <form
        onSubmit={handleSubmit((data) => onSubmit(data.email.trim().toLowerCase()))}
        className="ls-form-fields"
        noValidate
      >
        <div className="ls-input-group">
          <label className="ls-input-label">{t("email.emailLabel")}</label>
          <div className="auth-input-container ls-input-container">
            <Mail size={18} className="ls-input-icon" />
            <input
              {...register("email")}
              type="email"
              placeholder="you@dentalclinic.com"
              className="ls-form-input"
              disabled={loading}
              autoComplete="email"
              autoFocus
            />
          </div>
        </div>

        <button
          type="submit"
          className={`ls-btn-primary${loading ? " ls-btn-primary--disabled" : ""}`}
          disabled={loading}
        >
          {loading ? (
            <Loader2 size={18} className="ls-spinner" />
          ) : (
            <Mail size={18} />
          )}
          <span>{loading ? t("email.checking") : t("common:actions.continue")}</span>
        </button>
      </form>
    </>
  );
}
